import useFetch from "../../../../hooks/useFetch";

/**
 * Fetch the mentor's lightweight KYC status (whether PAN/bank are on file and their
 * verification state) — used to drive the "Action required" banner and edit routing.
 * GET /mentor/:mentorId/kyc-details
 * Returns: { hasPan, panStatus, hasBank, bankStatus }
 * @param {string} mentorId
 * @param {Object} [options] - onSuccess, onError, enabled, etc.
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useFetchKycStatus = (mentorId, options = {}) =>
  useFetch(`/mentor/${mentorId}/kyc-details`, { method: "get", ...options });

/**
 * Fetch the mentor's saved PAN details (PAN number masked) for edit-modal prefill.
 * GET /mentor/:mentorId/kyc/pan-details
 * Returns: { havingPan, nameAsPerPan, dobAsPerPan, panNumberMasked, verificationStatus }
 * @param {string} mentorId
 * @param {Object} [options] - onSuccess, onError, enabled, manual, etc.
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useFetchPanDetails = (mentorId, options = {}) =>
  useFetch(`/mentor/${mentorId}/kyc/pan-details`, {
    method: "get",
    ...options,
  });

/**
 * Fetch the mentor's saved bank details (account number masked) for edit-modal prefill.
 * GET /mentor/:mentorId/kyc/bank-details
 * Returns: { accountHolderName, mobileNumber, ifscCode, bankName, branchName, accountNumberMasked, accountType, verificationStatus }
 * @param {string} mentorId
 * @param {Object} [options] - onSuccess, onError, enabled, manual, etc.
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useFetchBankDetails = (mentorId, options = {}) =>
  useFetch(`/mentor/${mentorId}/kyc/bank-details`, {
    method: "get",
    ...options,
  });

/**
 * Create or update the mentor's PAN details (multipart) and trigger Cashfree PAN verification.
 * POST/PUT /mentor/:mentorId/kyc/pan — pass method: "post" (create) or "put" (update) via mutate().
 * Returns: { verificationStatus } — one of verified | aadhaar_not_linked | finance_review_required | failed | retry_required
 * @param {string} mentorId
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useSavePanDetails = (mentorId) =>
  useFetch(`/mentor/${mentorId}/kyc/pan`, { manual: true });

/**
 * Declare that the mentor has no PAN (Form 60 upload).
 * PUT /mentor/:mentorId/kyc/pan/no-pan
 * Returns: { success }
 * @param {string} mentorId
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useSaveNoPanDetails = (mentorId) =>
  useFetch(`/mentor/${mentorId}/kyc/pan/no-pan`, {
    method: "put",
    manual: true,
  });

/**
 * Create or update the mentor's bank details (multipart) and trigger Cashfree bank verification.
 * POST/PUT /mentor/:mentorId/kyc/bank — pass method: "post" (create) or "put" (update) via mutate().
 * Returns: { verificationStatus } — one of verified | finance_review_required | failed | retry_required
 * @param {string} mentorId
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useSaveBankDetails = (mentorId) =>
  useFetch(`/mentor/${mentorId}/kyc/bank`, { manual: true });

/**
 * Verify an IFSC code and resolve its bank/branch name.
 * POST /mentor/kyc/ifsc-verify
 * Returns: { success, bankName, branchName, msg }
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useVerifyIfsc = () =>
  useFetch("/mentor/kyc/ifsc-verify", { method: "post", manual: true });
